import React, { useCallback, useRef, useState } from "react";
import {
  StepContent,
  TabBar,
  Tab,
  UploadZone,
  UploadZoneIcon,
  UploadZoneText,
  UploadZoneHint,
  CodeEditor,
  CodeBlock,
  DetailSectionTitle,
} from "../styles";
import { inferSchemaFromSample } from "../constants";

interface ParsedSchema {
  fields: { name: string; type: string }[];
}

interface StepDefineSchemaProps {
  schemaTab: "upload" | "editor";
  onSchemaTabChange: (tab: "upload" | "editor") => void;
  parsedSchema: ParsedSchema | null;
  onSchemaChange: (schema: ParsedSchema | null) => void;
  editorContent: string;
  onEditorContentChange: (val: string) => void;
}

const StepDefineSchema: React.FC<StepDefineSchemaProps> = ({
  schemaTab,
  onSchemaTabChange,
  parsedSchema,
  onSchemaChange,
  editorContent,
  onEditorContentChange,
}) => {
  const [dragover, setDragover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result as string);
          const schema = inferSchemaFromSample(json);
          onSchemaChange(schema);
        } catch {
          onSchemaChange(null);
        }
      };
      reader.readAsText(file);
    },
    [onSchemaChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragover(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleEditorChange = useCallback(
    (val: string) => {
      onEditorContentChange(val);
      try {
        const parsed = JSON.parse(val);
        if (parsed && Array.isArray(parsed.fields)) {
          onSchemaChange(parsed);
        }
      } catch {
        // invalid JSON, don't update schema
      }
    },
    [onEditorContentChange, onSchemaChange],
  );

  return (
    <StepContent>
      <DetailSectionTitle>Define schema</DetailSectionTitle>
      <p>
        Provide a JSON schema describing your data fields, or upload a sample
        dataset to infer one.
      </p>
      <TabBar>
        <Tab
          $active={schemaTab === "upload"}
          onClick={() => onSchemaTabChange("upload")}
        >
          Upload Sample
        </Tab>
        <Tab
          $active={schemaTab === "editor"}
          onClick={() => onSchemaTabChange("editor")}
        >
          Manual Editor
        </Tab>
      </TabBar>

      {schemaTab === "upload" && (
        <>
          <UploadZone
            $dragover={dragover}
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              setDragover(true);
            }}
            onDragLeave={() => setDragover(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadZoneIcon>{"\u{1F4C4}"}</UploadZoneIcon>
            <UploadZoneText>
              Drop a JSON sample file here, or click to browse
            </UploadZoneText>
            <UploadZoneHint>
              {"We'll infer the schema from your sample data"}
            </UploadZoneHint>
          </UploadZone>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          {parsedSchema && (
            <>
              <DetailSectionTitle>Inferred schema</DetailSectionTitle>
              <CodeBlock>{JSON.stringify(parsedSchema, null, 2)}</CodeBlock>
            </>
          )}
        </>
      )}

      {schemaTab === "editor" && (
        <CodeEditor
          value={editorContent}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handleEditorChange(e.target.value)
          }
        />
      )}
    </StepContent>
  );
};

export default StepDefineSchema;
